/**
 * Where the bare address `/` leads (feature plan 0068). Pure, no DI imports: routes.ts calls it from the
 * `home` route, the settings page "Allgemein" lists the same views, and both are unit tested without the
 * app's component graph.
 *
 * A tenant admin may save one of the views below as the tenant's start page (`save.tenant-settings`);
 * HomeAction puts it into the page as `window.__TENANT_SETTINGS__.startPage`. A saved start page ALWAYS
 * wins over the browser's last used mode (Anke, 2026-10-30); without one — or with a name that is not
 * (any more) in the list — `/` keeps opening the last used mode as before.
 *
 * Feature plan 0069: under Linkview and Wissen the tenant may also pick one concrete Linkboard or
 * knowledge object (`startPageTarget`, a ShortId). HomeAction passes it on only while the target exists;
 * a deleted one arrives as `startPageTargetGone` instead, and `/` then opens the area.
 */

/** One selectable start page: the route name that is saved, and the path `/` goes to. */
export interface StartPage {
    readonly name: string;
    readonly path: string;
}

/** The views of one mode, labelled like the mode switch. */
export interface StartPageGroup {
    readonly labelKey: string;
    readonly pages: readonly StartPage[];
}

/**
 * The selectable views, grouped in the order of the mode switch, each group in its menu order
 * (index.html's ToDo menu, `PERSONAL_ORGANIZER_NAV`). Left out on purpose: "Meine Ansicht" (`/by-me`,
 * behind a build flag) and "Daily + Events" (in the menu, but without a route). The labels are the
 * `pageTitle.<name>` texts (feature plan 0067); page-title.keys.test.ts-style tests pin both.
 */
export const START_PAGE_GROUPS: readonly StartPageGroup[] = [
    {
        labelKey: 'wissen.todo-label',
        pages: [
            {name: 'tasklist', path: '/tasklist'},
            {name: 'absence-list', path: '/absence-list'},
            {name: 'by-board', path: '/by-board'},
            {name: 'by-daily', path: '/by-daily'},
            {name: 'by-label', path: '/by-label'},
            {name: 'by-assignee', path: '/by-assignee'},
            {name: 'by-creator', path: '/by-creator'},
            {name: 'by-type', path: '/by-type'},
            {name: 'by-issue-status', path: '/by-issue-status'},
        ],
    },
    {
        labelKey: 'wissen.resource-label',
        pages: [{name: 'resource-management-view', path: '/resource-management'}],
    },
    {
        labelKey: 'wissen.mode-label',
        pages: [{name: 'knowledge', path: '/knowledge'}],
    },
    {
        labelKey: 'wissen.linkview-label',
        pages: [{name: 'linkview', path: '/linkview'}],
    },
    {
        labelKey: 'wissen.personal-organizer-label',
        pages: [
            {name: 'personal-organizer-calendar', path: '/personal-organizer'},
            {name: 'personal-organizer-boxes', path: '/personal-organizer/boxes'},
        ],
    },
    {
        labelKey: 'wissen.gimmicks-label',
        pages: [{name: 'gimmicks', path: '/gimmicks'}],
    },
];

const START_PAGES: readonly StartPage[] = START_PAGE_GROUPS.flatMap(group => group.pages);

/**
 * The path of a selectable start page, or `undefined`. A plain search by equality, not a property
 * lookup, so names like `__proto__` or `toString` find nothing.
 */
export function startPagePath(name: unknown): string | undefined {
    return typeof name === 'string'
        ? START_PAGES.find(page => page.name === name)?.path
        : undefined;
}

/**
 * The areas whose start page may name a concrete target, and the path the target's ShortId is appended
 * to. The server holds the matching route → table map (StartPageTargets.php); tests pin both sides to the
 * same two names. Searched by equality like START_PAGES.
 */
const START_PAGE_TARGET_PREFIXES: readonly { readonly name: string; readonly prefix: string }[] = [
    {name: 'linkview', prefix: '/linkview/'},
    {name: 'knowledge', prefix: '/knowledge/'},
];

/** Form of a ShortId, as the server checks it (22 Base64URL characters). */
const SHORT_ID = /^[A-Za-z0-9_-]{22}$/;

/** Whether this start page (a route name) may name a concrete Linkboard or knowledge object. */
export function startPageAcceptsTarget(name: unknown): boolean {
    return START_PAGE_TARGET_PREFIXES.some(entry => entry.name === name);
}

/** Where `/` goes when no start page is saved: the last used mode of this browser (unchanged since #73). */
function lastModePath(lastMode: string | null): string {
    switch (lastMode) {
        case 'wissen':
            return '/knowledge';
        case 'resource':
            return '/resource-management';
        case 'linkview':
            return '/linkview';
        case 'personal-organizer':
            return '/personal-organizer';
        case 'gimmicks':
            return '/gimmicks';
        default:
            return '/by-board';
    }
}

/**
 * The target of `/`: the tenant's concrete target if one is saved under a start page that takes one, else
 * the start page if it names a selectable view, else the last used mode.
 *
 * @param settings the injected `window.__TENANT_SETTINGS__` (may be missing in tests or an old shell)
 * @param lastMode `localStorage.lastMode`
 */
export function homeTarget(settings: Window['__TENANT_SETTINGS__'] | null, lastMode: string | null): string {
    const startPage = settings?.startPage;
    const target = settings?.startPageTarget;
    const prefix = START_PAGE_TARGET_PREFIXES.find(entry => entry.name === startPage)?.prefix;
    if (prefix && typeof target === 'string' && SHORT_ID.test(target)) {
        return prefix + target;
    }
    return startPagePath(startPage) ?? lastModePath(lastMode);
}
